import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useScopeSet } from '@/auth/useScope';
import { usePanelStore } from '@/panels/panelStore';
import { useIsMobile } from '@/shared/useIsMobile';
import { useReducedMotionSafe } from '@/shared/MotionWrappers';
import { useAppStore } from './appStore';
import { navConfig, type NavItem } from './navConfig';

function itemAllowed(item: NavItem, scopes: ReadonlySet<string>): boolean {
  return item.scopes.every((s) => scopes.has(s));
}

type LeftNavProps = {
  /** Mobile sheet open state (ignored on desktop). */
  mobileOpen?: boolean;
  /** Called when the mobile sheet requests to close. */
  onClose?: () => void;
};

export function LeftNav({ mobileOpen = false, onClose }: LeftNavProps) {
  const { t } = useTranslation('nav');
  const { t: tc } = useTranslation('common');
  const scopes = useScopeSet();
  const collapsed = useAppStore((s) => s.leftNavCollapsed);
  const openSurface = usePanelStore((s) => s.openSurface);
  const isMobile = useIsMobile();
  const reduced = useReducedMotionSafe();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(navConfig.map((section) => [section.labelKey, true])),
  );

  const visibleSections = useMemo(
    () =>
      navConfig
        .map((section) => ({
          ...section,
          items: section.items.filter((i) => itemAllowed(i, scopes)),
        }))
        .filter((s) => s.items.length > 0),
    [scopes],
  );

  // Close on Escape key when the mobile sheet is open
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, mobileOpen, onClose]);

  // Desktop: collapsed state hides nav entirely
  if (!isMobile && collapsed) return null;

  function toggleSection(key: string) {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleItemClick(surface: string, labelKey: string) {
    openSurface(surface, undefined, { title: t(`surfaces.${labelKey}`) });
    // On mobile the nav drawer closes after selection; on desktop it stays docked.
    if (isMobile) {
      onClose?.();
    }
  }

  const navContent = (
    <nav
      aria-label="Primary navigation"
      className="flex h-full w-60 shrink-0 flex-col overflow-y-auto border-r border-border bg-card text-sm"
    >
      <ul className="flex flex-col py-2">
        {visibleSections.map((section) => {
          const open = openSections[section.labelKey] ?? true;
          return (
            <li key={section.labelKey} className="px-2">
              <button
                type="button"
                onClick={() => toggleSection(section.labelKey)}
                className="flex w-full items-center gap-1 rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted"
                aria-expanded={open}
              >
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {section.icon !== undefined ? (
                  <section.icon size={12} aria-hidden="true" />
                ) : null}
                {t(`sections.${section.labelKey}`)}
              </button>
              {open && (
                <ul className="mt-1 flex flex-col gap-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.surface}>
                        <button
                          type="button"
                          onClick={() => handleItemClick(item.surface, item.labelKey)}
                          className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-left hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <Icon size={14} className="text-muted-foreground" />
                          <span className="truncate">
                            {t(`surfaces.${item.labelKey}`, {
                              defaultValue: item.labelKey,
                            })}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );

  // Desktop: render inline as before
  if (!isMobile) {
    return navContent;
  }

  // Mobile: render as a fixed sheet with backdrop
  const slideVariants: Variants = reduced
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        hidden: { x: '-100%' },
        visible: { x: 0, transition: { duration: 0.2, ease: 'easeOut' as const } },
        exit: { x: '-100%', transition: { duration: 0.15, ease: 'easeIn' as const } },
      };

  return (
    <AnimatePresence>
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="nav-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduced ? 0 : 0.2 }}
            className="fixed inset-0 z-40 bg-black/50"
            aria-hidden="true"
            onClick={onClose}
          />
          {/* Sheet */}
          <motion.div
            key="nav-sheet"
            variants={slideVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-y-0 left-0 z-50 flex"
            role="dialog"
            aria-modal="true"
            aria-label={tc('mobile.navLabel', { defaultValue: 'Navigation' })}
          >
            {navContent}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
